import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import React, { useEffect, useState } from "react";
import {
  fetchPreviousReturnListData,
  ReturnTransaction,
} from "../../../../../store/slices/PreviousReturnSlice";
import { formatCurrency, getFormattedDate } from "../../../helper";
import { getCostInterval } from "../../../../../pages/Checkout/CheckoutHelper";
import { MotifIcon, MotifTooltip } from "@ey-xd/motif-react";
import "./PreviousReturn.scss";
import productListEmptyImg from "../../../../../assets/images/empty_product_list_default.svg";
import {
  IconoirTimer,
  IconoirCheck,
  IconoirHelpCircle,
  IconoirNavArrowUp,
  IconoirNavArrowDown,
} from "@ey-xd/motif-icon";
import ErrorOrderPage from "../../../../ErrorOrderPage/ErrorOrderPage";
import EmptyOrder from "../../../../EmptyOrder";
import Loader from "../../../../Loader";
type PreviousReturnProps = {
  renderHeaderWithTooltip: (
    header: string,
    tooltip: string | JSX.Element,
  ) => JSX.Element;
  renderUsagePeriodInTwoLines: (
    startDate: string,
    endDate: string,
  ) => JSX.Element;
  getUnitsUsed: (costMetricCd: string, availableUnits: number) => string;
  orderSkuId: number | undefined;
};

export default function PreviousReturn({
  renderHeaderWithTooltip,
  renderUsagePeriodInTwoLines,
  getUnitsUsed,
  orderSkuId,
}: PreviousReturnProps) {
  const dispatch = useDispatch();
  const previousReturnListData = useSelector(
    (state: RootState) => state.PreviousReturnSlice,
  );
  const { isLoading, error, data } = previousReturnListData;
  const resturnHistoryData = data === null ? [] : data;
  const [openDetailIds, setOpenDetailIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    // Dispatch action to fetch previous return data here
    dispatch(
      fetchPreviousReturnListData({
        Action: "retrieveReturnHistory",
        OrderSkuId: Number(orderSkuId),
      }) as any,
    );
  }, [dispatch]);
  const getFormattedTransactionStatus = (status: string, reason: string) => {
    switch (status) {
      case "Approved":
        return (
          <div className="return-approved">
            <div>{status}</div>
            <div>
              <MotifIcon iconFunction={IconoirCheck} />
            </div>
          </div>
        );
      case "In approval":
        return (
          <div className="return-pending">
            <div>{status}</div>
            <div>
              <MotifIcon iconFunction={IconoirCheck} />
            </div>
          </div>
        );
      case "Partially approved":
        return (
          <div className="return-approved">
            <div>{status}</div>
            <div>
              <MotifIcon iconFunction={IconoirCheck} />
            </div>
          </div>
        );
      case "Rejected":
        return (
          <>
            <MotifTooltip
              contentClassName="tooltip-content"
              hideArrow={true}
              trigger={
                <div className="return-rejected">
                  <div>{status}</div>
                  <div className="tooltip-icon">
                    <MotifIcon iconFunction={IconoirHelpCircle} />
                  </div>
                </div>
              }
            >
              <>
                Rejection Reason
                <div
                  className="motif-caption-default-light returnsRejectToolTip"
                  style={{
                    marginTop: "6px",
                  }}
                >
                  {reason}
                </div>
              </>
            </MotifTooltip>
          </>
        );
      case "Pending":
        return (
          <div className="return-pending">
            <div>{status}</div>
            <div>
              <MotifIcon iconFunction={IconoirTimer} />
            </div>
          </div>
        );
      default:
        return status;
    }
  };

  const viewDetailHandler = (returnData: ReturnTransaction) => {
    const id = "" + returnData.TransactionReturnId;
    setOpenDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  if (error !== "" && error !== null) {
    return (
      <div className="error-message-section">
        <ErrorOrderPage
          image={productListEmptyImg}
          header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
          handleRefresh={() => {
            dispatch(
              fetchPreviousReturnListData({
                Action: "retrieveReturnHistory",
                OrderSkuId: Number(orderSkuId),
              }) as any,
            );
          }}
        />
      </div>
    );
  }

  if (
    !isLoading &&
    (data === null || (Array.isArray(data) && data.length === 0))
  ) {
    return (
      <div className="error-message-section">
        <EmptyOrder
          image={productListEmptyImg}
          header="No previous return data available."
          subHeader="There are no previous return records to display for this order."
        />
      </div>
    );
  }
  return (
    <div className="previous-returns-table-container">
      <div className="table-container request-table-container">
        <table className="motif-table-standard request-table">
          <thead>
            <tr>
              <th>
                {renderHeaderWithTooltip(
                  "Return ID",
                  "The unique identifier for the return transaction.",
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Usage period",
                  "The start and end dates during which the SKU is used.",
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Usage interval",
                  "Shows two values: the number of consecutive transactions and the total number of requested periods.",
                )}
              </th>
              <th className="text-right">
                {renderHeaderWithTooltip(
                  "Unit cost",
                  <>
                    Price you will pay for each unit you are ordering. These are
                    the unit type of products:
                    <ul>
                      <li>GBs</li>
                      <li>Hours</li>
                      <li>Licences</li>
                      <li>Instance</li>
                      <li>Tokens</li>
                      <li>Units</li>
                      <li>
                        One time - example would be setup fee to enable a
                        product ready for use.
                      </li>
                    </ul>
                  </>,
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Units used",
                  "The number of units and cost consumed during the usage period. For variable SKUs, the ordered units may differ from the consumed units.",
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Return units",
                  "The number of units and cost requested in the return.",
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Approved Units",
                  "The number of units and cost that were accepted based on the requested return.",
                )}
              </th>
              <th>
                {renderHeaderWithTooltip(
                  "Status",
                  <>
                    Indicates the current state of the return request. Possible
                    statuses are:
                    <ul>
                      <li>
                        In approval – The return request is still under review.
                      </li>
                      <li>Approved – The return request has been accepted.</li>
                      <li>
                        Rejected – The return request was not accepted and can
                        be submitted again.
                      </li>
                    </ul>
                  </>,
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9}>
                  <Loader />
                </td>
              </tr>
            ) : (
              resturnHistoryData.map((returnData: ReturnTransaction) => (
                <React.Fragment key={returnData.TransactionReturnId}>
                  <tr
                    className={`${openDetailIds.has("" + returnData.TransactionReturnId) ? "expanded-row" : ""}`}
                  >
                    <td>
                      <div className="return-id-cell">
                        <div>{returnData.TransactionReturnId}</div>
                        <div
                          className="view-detail"
                          onClick={() => viewDetailHandler(returnData)}
                          role="button"
                          tabIndex={0}
                          aria-expanded={openDetailIds.has(
                            "" + returnData.TransactionReturnId,
                          )}
                          aria-label={
                            openDetailIds.has(
                              "" + returnData.TransactionReturnId,
                            )
                              ? "Close details"
                              : "View details"
                          }
                        >
                          <div className="view-detail-text">
                            {openDetailIds.has(
                              "" + returnData.TransactionReturnId,
                            )
                              ? "Close details"
                              : "View details"}
                          </div>
                          <div className="icon">
                            {openDetailIds.has(
                              "" + returnData.TransactionReturnId,
                            ) ? (
                              <MotifIcon
                                iconFunction={IconoirNavArrowUp}
                                size="20"
                                fill="#85B9FD"
                              />
                            ) : (
                              <MotifIcon
                                iconFunction={IconoirNavArrowDown}
                                size="20"
                                fill="#85B9FD"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="usage-period-cell">
                      {renderUsagePeriodInTwoLines(
                        getFormattedDate(returnData.IntervalStartDtm),
                        getFormattedDate(returnData.IntervalEndDtm),
                      )}
                    </td>
                    <td className="usage-interval-cell">{`${returnData.IntervalNum} of ${returnData.Intervals}`}</td>
                    <td className="text-right">
                      <div>{formatCurrency(returnData.UnitCost)}</div>
                      <div className="secondary-value">
                        {getCostInterval(returnData.CostIntervalCd)}
                      </div>
                    </td>
                    <td>
                      <div>{`${returnData.OrgQuantity === null ? "--" : returnData.OrgQuantity + " " + getUnitsUsed(returnData.CostMetricCd, returnData.OrgQuantity)} `}</div>
                      <div className="secondary-value">{`${formatCurrency(returnData.OrgTotalCost)}`}</div>
                    </td>
                    <td>
                      <div>{`${returnData.ReturnQuantity === null ? "--" : returnData.ReturnQuantity + " " + getUnitsUsed(returnData.CostMetricCd, returnData.ReturnQuantity)} `}</div>
                      <div className="secondary-value">{`${returnData.ReturnCost === null ? "" : formatCurrency(returnData.ReturnCost)}`}</div>
                    </td>
                    <td>
                      <div>{`${returnData.ApprovedQuantity === null ? "--" : returnData.ApprovedQuantity + " " + getUnitsUsed(returnData.CostMetricCd, returnData.ApprovedQuantity)} `}</div>
                      <div className="secondary-value">{`${returnData.ApprovedCost === null ? "" : formatCurrency(returnData.ApprovedCost)}`}</div>
                    </td>
                    <td>
                      <div className="order-status">
                        {getFormattedTransactionStatus(
                          returnData.ReturnTransactionStatus,
                          returnData.RejectReason || "",
                        )}
                      </div>
                    </td>
                  </tr>
                  {openDetailIds.has("" + returnData.TransactionReturnId) && (
                    <tr>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <div className="view-detail-container">
                          <table className="motif-table-standard request-table">
                            <thead>
                              <tr>
                                <th>Request date</th>
                                <th>Return reason</th>
                                {returnData.ReturnTransactionStatus ===
                                  "Rejected" && <th>Rejection reason</th>}
                              </tr>
                            </thead>
                            <tbody>
                              <tr key={returnData.TransactionReturnId}>
                                <td>
                                  {getFormattedDate(
                                    returnData.ReturnRequestDate,
                                  )}
                                </td>
                                <td>{returnData.ReturnReason}</td>
                                {returnData.ReturnTransactionStatus ===
                                  "Rejected" && (
                                  <td>{returnData.RejectReason}</td>
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
